import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidatorFn,
  FormControl,
} from '@angular/forms';
import { UsersService } from 'src/app/services/users/users.service';
import { UserInfo } from '../../models/user-info.model';
import { Router } from '@angular/router';
import { entAnimations } from 'src/app/animations';
import { TranslateService } from '@ngx-translate/core';
import { MatRadioChange } from '@angular/material/radio';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { Error } from 'src/app/models/error.model';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomValidators } from 'src/app/utils/validators';
import { LocalStorageService } from 'src/app/services/localStorage/localStorage.service';
import { UserActivateInfo } from 'src/app/models/user-activate-info.model';

@Component({
  selector: 'app-validation',
  templateUrl: './validation.component.html',
  styleUrls: ['./validation.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: entAnimations,
  standalone: false,
})
export class ValidationComponent implements OnInit {
  isUserUpdated = false;
  errorUpdate = false;
  form: FormGroup;
  error: Error = { error: { message: '', code: '' } };
  currentUser: UserInfo = new UserInfo();
  updatedUser: UserActivateInfo;
  hasMultipleFirstName: boolean;
  hasMultipleLastName: boolean;
  phoneValidator = '^[0-9]*$';
  activationCode: string;

  maxlength = 30;
  userPreferedLanguage: string;
  isBusy = false;
  userAccountname = '';
  selectedTab: number;

  password: FormControl;
  passwordConfirmation: FormControl;

  // F2A Auth
  displayPhone: boolean;

  spinner = {
    color: 'primary',
    mode: 'indeterminate' as ProgressSpinnerMode,
    value: 50,
    diameter: 50,
  };

  preferredLanguage = [
    {
      id: 1,
      name: 'Français',
      acronym: 'fr',
    },
    {
      id: 2,
      name: 'Deutsch',
      acronym: 'de',
    },
  ];

  genderFR = [
    {
      id: 1,
      name: 'Homme',
      acronym: 'M',
    },
    {
      id: 2,
      name: 'Femme',
      acronym: 'F',
    },
  ];

  genderDE = [
    {
      id: 1,
      name: 'Mann',
      acronym: 'M',
    },
    {
      id: 2,
      name: 'Frau',
      acronym: 'F',
    },
  ];

  // Horizontal Stepper
  horizontalStepperStep1: FormGroup;
  horizontalStepperStep2: FormGroup;
  horizontalStepperStep3: FormGroup;
  horizontalStepperStep4: FormGroup;
  horizontalStepperStep5: FormGroup;

  /**
   * Constructor
   *
   * @param {FormBuilder} _formBuilder
   */
  constructor(
    private _formBuilder: FormBuilder,
    private _userService: UsersService,
    private router: Router,
    private _translateService: TranslateService,
    private _localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    if (this._userService.currentUserValue == null) {
      const startUser =
        this._localStorageService.getItem<UserInfo>('startUser');
      if (startUser) {
        this._userService.currentUserSubject.next(startUser);
      } else {
        this.router.navigate(['/start']);
      }
    }

    this.horizontalStepperStep1 = this._formBuilder.group({
      avs: [{ value: '', disabled: true }],
      officialFirstName: [{ value: '', disabled: true }],
      officialLastName: [{ value: '', disabled: true }],
      dateOfBirth: [{ value: '', disabled: true }],
      gender: [{ value: '', disabled: true }],
    });

    this.horizontalStepperStep2 = this._formBuilder.group({
      firstName: [''],
      lastName: [''],
      preferredLanguage: ['', Validators.required],
    });

    this.horizontalStepperStep3 = this._formBuilder.group({
      f2a: ['', Validators.required],
      defaultDial: [''],
      phone: [
        '',
        [
          Validators.required,
          Validators.pattern(this.phoneValidator),
          CustomValidators.phoneValidation(),
          Validators.minLength(9),
        ],
      ],
      f2aCondition: ['', Validators.required],
    });

    this.horizontalStepperStep4 = this._formBuilder.group(
      {
        password: this.password,
        passwordConfirmation: this.passwordConfirmation,
      },
      { validators: [CustomValidators.confirmPasswords()] }
    );

    this.horizontalStepperStep5 = this._formBuilder.group({
      email: [
        '',
        [
          Validators.required,
          CustomValidators.validEmail(),
          CustomValidators.EmailInterdiction(),
        ],
      ],
    });

    // Get current user
    this.currentUser = this._localStorageService.getItem<UserInfo>('startUser');
    this.activationCode =
      this._localStorageService.getItem<string>('activationCode');

    // F2A
    this.displayPhone = false;

    this.setDefaultFormValue();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  fetchFormData(): void {
    let userPhone: string = null;
    if (this.displayPhone) {
      userPhone =
        this.horizontalStepperStep3.get('defaultDial').value +
        this.horizontalStepperStep3.get('phone').value;
    }
    this.userPreferedLanguage =
      this.horizontalStepperStep2.get('preferredLanguage').value.name;

    this.updatedUser = {
      id: this.currentUser.id,
      oid: this.currentUser.oid,
      firstName: this.horizontalStepperStep2.get('firstName').value,
      lastName: this.horizontalStepperStep2.get('lastName').value,
      preferredLanguage: this.userPreferedLanguage,
      email: this.horizontalStepperStep5.get('email').value,
      twoFactorAuthType: this.horizontalStepperStep3.get('f2a').value,
      password: this.horizontalStepperStep4.get('password').value,
      phone: userPhone,
    };

    // Reset all errors and boolean in case of multiple error
    this.isBusy = false;
    this.isUserUpdated = false;
    this.errorUpdate = false;
  }

  setDefaultFormValue(): void {
    // Set Default value for Step 1
    this.horizontalStepperStep1.get('avs').setValue(this.currentUser.avs);
    this.horizontalStepperStep1
      .get('officialFirstName')
      .setValue(this.currentUser.officialFirstName);
    this.horizontalStepperStep1
      .get('officialLastName')
      .setValue(this.currentUser.officialLastName);
    this.horizontalStepperStep1
      .get('dateOfBirth')
      .setValue(this.currentUser.dateOfBirth);
    this.horizontalStepperStep1.get('gender').setValue(this.currentUser.gender);

    if (this.currentUser.preferredLanguage === 'de') {
      const genderType = this.genderDE.find(
        gender => gender.acronym === this.currentUser.gender
      );
      if (genderType) {
        this.horizontalStepperStep1.get('gender').setValue(genderType.name);
      }
    } else if (this.currentUser.preferredLanguage === 'fr') {
      const genderType = this.genderFR.find(
        gender => gender.acronym === this.currentUser.gender
      );
      if (genderType) {
        this.horizontalStepperStep1.get('gender').setValue(genderType.name);
      }
    }

    // Set Default value for Step 2

    this.horizontalStepperStep2
      .get('firstName')
      .setValue(this.currentUser.firstName);
    this.horizontalStepperStep2
      .get('lastName')
      .setValue(this.currentUser.lastName);

    // Set Default value for Step 3
    this.currentUser.phone = '';
    this.horizontalStepperStep3.get('phone').setValue(this.currentUser.phone);

    // Set Default value for Step 5
    this.horizontalStepperStep5.get('email').setValue(this.currentUser.email);

    // Get Default Language
    const toSelect = this.preferredLanguage.find(
      lang => lang.acronym === this.currentUser.preferredLanguage
    );
    this.horizontalStepperStep2.get('preferredLanguage').setValue(toSelect);

    // Set Default Dial for Country Phone number
    this.horizontalStepperStep3.get('defaultDial').setValue('+41');
    this.horizontalStepperStep3.get('f2a').setValue('App');
    this.displayPhone = true;

    // Check Multiple Name
    if (this.currentUser.officialFirstName.indexOf(' ') === -1) {
      if (this.currentUser.officialFirstName.indexOf('-') > 0) {
        this.hasMultipleFirstName = true;
        this.horizontalStepperStep2.get('firstName').setValue('');
        this.horizontalStepperStep2
          .get('firstName')
          .setValidators([
            Validators.required,
            CustomValidators.nameValidator(this.currentUser.officialFirstName),
          ]);
      } else {
        this.hasMultipleFirstName = false;
        this.horizontalStepperStep2.get('firstName').disable();
        this.horizontalStepperStep2
          .get('firstName')
          .setValidators(Validators.required);
      }
    } else {
      this.hasMultipleFirstName = true;
      this.horizontalStepperStep2.get('firstName').setValue('');
      this.horizontalStepperStep2
        .get('firstName')
        .setValidators([
          Validators.required,
          CustomValidators.nameValidator(this.currentUser.officialFirstName),
        ]);
    }

    if (this.currentUser.officialLastName.indexOf(' ') === -1) {
      if (this.currentUser.officialLastName.indexOf('-') > 0) {
        this.hasMultipleLastName = true;
        this.horizontalStepperStep2.get('lastName').setValue('');
        this.horizontalStepperStep2
          .get('lastName')
          .setValidators([
            Validators.required,
            CustomValidators.nameValidator(this.currentUser.officialLastName),
          ]);
      } else {
        this.hasMultipleLastName = false;
        this.horizontalStepperStep2.get('lastName').disable();
        this.horizontalStepperStep2
          .get('lastName')
          .setValidators(Validators.required);
      }
    } else {
      this.hasMultipleLastName = true;
      this.horizontalStepperStep2.get('lastName').setValue('');
      this.horizontalStepperStep2
        .get('lastName')
        .setValidators([
          Validators.required,
          CustomValidators.nameValidator(this.currentUser.officialLastName),
        ]);
    }

    this.horizontalStepperStep4
      .get('password')
      .setValidators([
        Validators.required,
        CustomValidators.passwordNameValidator(
          this.currentUser.officialFirstName,
          this.currentUser.officialLastName
        ),
        CustomValidators.passwordRestriction(),
        this.passwordInterdiction(this.activationCode),
        Validators.minLength(10),
        Validators.maxLength(50),
      ]);

    this.horizontalStepperStep4
      .get('passwordConfirmation')
      .setValidators([
        Validators.required,
        CustomValidators.passwordRestriction(),
        this.passwordInterdiction(this.activationCode),
        CustomValidators.passwordNameValidator(
          this.currentUser.officialFirstName,
          this.currentUser.officialLastName
        ),
        Validators.minLength(10),
        Validators.maxLength(50),
      ]);
  }

  changeF2A(mrChange: MatRadioChange): void {
    if (mrChange.value === 'Token') {
      this.displayPhone = false;
      this.horizontalStepperStep3.removeControl('phone');
    } else {
      this.displayPhone = true;
      this.horizontalStepperStep3.addControl(
        'phone',
        new FormControl('', [
          Validators.required,
          Validators.pattern(this.phoneValidator),
          CustomValidators.phoneValidation(),
          Validators.minLength(9),
        ])
      );
    }

    if (mrChange.value === 'App') {
      this.selectedTab = 0;
    }
    if (mrChange.value === 'SMS') {
      this.selectedTab = 1;
    }
    if (mrChange.value === 'Token') {
      this.selectedTab = 2;
    }
  }

  getErrorMessage(): string {
    return this.horizontalStepperStep4.get('password').hasError('required')
      ? this._translateService.instant('ERROR.REQUIRED')
      : this.horizontalStepperStep4
            .get('password')
            .hasError('passwordRestriction')
        ? this._translateService.instant('ERROR.COMPLEXITY')
        : this.horizontalStepperStep4
              .get('password')
              .hasError('passwordInterdiction')
          ? this._translateService.instant('ERROR.INTERDICTION')
          : this.horizontalStepperStep4
                .get('password')
                .hasError('passwordContainName')
            ? this._translateService.instant('ERROR.CONTAINNAME')
            : this.horizontalStepperStep4.get('password').hasError('minlength')
              ? this._translateService.instant('ERROR.MIN')
              : this.horizontalStepperStep4
                    .get('password')
                    .hasError('maxlength')
                ? this._translateService.instant('ERROR.MAX')
                : '';
  }

  passwordInterdiction(activationCode: string): ValidatorFn {
    return (control: AbstractControl): Record<string, boolean> | null => {
      // Pwd must not be equals to ActivationCode
      if (control.value === activationCode) {
        return {
          passwordInterdiction: true,
        };
      }
    };
  }

  /**
   * Finish the horizontal stepper
   */
  finishHorizontalStepper() {
    try {
      this.fetchFormData();

      this.updatedUser.preferredLanguage =
        this.horizontalStepperStep2.get('preferredLanguage').value.acronym;

      this.isUserUpdated = false;
      this.errorUpdate = false;
      this.isBusy = true;

      this._userService.activateUser(this.updatedUser).subscribe({
        next: user => {
          this.userAccountname = user.username;
          this._userService.emptyStorage();
          this.isUserUpdated = true;
        },
        error: (error: HttpErrorResponse) => {
          if (error.statusText == 'Unknown Error') {
            this.error = { error: { message: 'Unknown Error', code: 'ERR2' } };
          } else if (error.status === 400) {
            this.error = {
              error: { message: 'Validation Error', code: 'ERR402' },
            };
          } else if (error.status === 401) {
            this._userService.emptyStorage();
            this.router.navigate(['/start']);
          } else {
            if (error.error) {
              if (typeof error.error === 'object') {
                this.error = error.error;
              } else if (typeof error.error === 'string') {
                this.error = JSON.parse(error.error);
              } else {
                this.error = {
                  error: { message: 'Unknown Error', code: 'ERR2' },
                };
              }
            } else {
              this.error = {
                error: {
                  message: 'Username or password is incorrect',
                  code: 'ERR2',
                },
              };
            }
          }
          this.errorUpdate = true;
          this.isBusy = false;
        },
        complete: () => {
          this.isBusy = false;
        },
      });
    } catch {
      this.error = { error: { message: 'Unknown Error', code: 'ERR2' } };
      this.errorUpdate = true;
      this.isBusy = false;
    }
  }
}
